import Component from 'core/Component';
import { Event } from 'services/EventEmitter';
import { getResources } from 'services/Resources';
import { on, off } from 'toolbox/event';
import { deepMerge } from 'toolbox/deepMerge';
import CrossRequestStorage from 'services/CrossRequestStorage';

/**
 * This is a description of the Registration constructor function.
 * @class
 * @classdesc This is a description of the Registration class.
 * @extends Component
 */
export default class Registration extends Component {
    /**
     * Constructor of the class that mainly merge the options of the components
     *
     * @param {HTMLElement} element HTMLElement of the component
     * @param {Object} options That belongs to the component
     */
    constructor(element, options = {}) {
        super(element, deepMerge({
            reloadData: {
                section: 'account',
                id: 'registration',
            },
            // reload page if a customer is successful registered
            reloadOnSuccess: true,
            redirectUrl: null,
            closeOnSigninOpen: false, // close registartion modal if SignIn was opened before as stackable
            crossRequestDelayedAction: null, // delayed action, will be applied after login
        }, options));
    }

    /**
     * All selectors must be cached. Never cache elements that are out of the component scope
     */
    initCache() {
        this.selectors.signInLink = this.element.querySelector('[data-js-signin]');
        this.selectors.socialLoginButtons = this.element.querySelectorAll('[data-js-sociallogin]');
    }

    /**
     * Should contain only event listeners and nothing else
     * All the event handlers should be into a separated function. No usage of anonymous function
     */
    bindEvents() {
        on('form.submit.success', this.element, this.onFormSubmitSuccess.bind(this));
        if (this.selectors.signInLink) {
            on('click', this.selectors.signInLink, this.openSignInModal.bind(this));
        }
        if (this.selectors.socialLoginButtons.length > 0) {
            on('click', this.selectors.socialLoginButtons, this.onSocialButtonClick.bind(this));
        }
    }

    /**
     * Form submit success event handler
     *
     * @param {Object} e Event object
     */
    onFormSubmitSuccess(e) { // eslint-disable-line complexity
        const content = e.detail && e.detail.content ? e.detail.content : {};
        const custom = content.custom || {};
        const analytics = this.analytics || {};
        const label = analytics.label || 'my account';

        e.stopPropagation();

        Event.emit('registration.success');

        // send analytics if the customer has been successfully subscribed
        if (custom.isNewsletterSubscribed) {
            const analyticsObject = {
                category: 'registration',
                action: 'newsletter',
                label,
                extraData: {
                    event_name: 'newsletter_registration',
                    referral: label,
                },
            };
            if (custom.registrationSource) {
                analyticsObject.label = custom.registrationSource;
                analyticsObject.extraData.referral = custom.registrationSource;
            }
            Event.emit('analytics.event', analyticsObject);
        }

        // send analytics if customer has been successfully enrolled to loyalty
        if (custom.isLoyaltyEnrolled) {
            const analyticsObject = {
                event: 'uaevent',
                category: 'registration',
                action: 'loyalty program',
                label,
                extraData: {
                    event_name: 'loyalty_program_registration',
                    referral: label,
                },
            };
            if (custom.registrationSource) {
                analyticsObject.label = custom.registrationSource;
                analyticsObject.extraData.referral = custom.registrationSource;
            }
            Event.emit('analytics.event', analyticsObject);
        }

        // emit global update event
        if (custom.pageUpdateData) {
            Event.emit('global.update', custom.pageUpdateData);
        }

        if (!content.redirectUrl) {
            setTimeout(this.redirectOrReload.bind(this, this.options), 200);
        }
    }

    /**
     * Social button click event handler
     */
    onSocialButtonClick() {
        if (this.options.crossRequestDelayedAction) {
            CrossRequestStorage.setDelayedActionForSocialLogin(this.options.crossRequestDelayedAction);
        }
    }

    /**
     * Redirects the user to the specified URL after a successful login.
     *
     * @param {object} options Component options
     */
    redirectOrReload(options) {
        // redirect to provided url if any
        if (options.redirectUrl) {
            if (options.redirectUrl.indexOf('redirectToOrigin') !== -1) {
                options.redirectUrl = `${options.redirectUrl}&redirectToOriginEnd=true`;
            }
            window.location.assign(options.redirectUrl);
        } else if (options.reloadOnSuccess) {
            window.location.reload();
        }
    }

    /**
     * Open modal pop-up for registration
     *
     * @returns {Promise} object
     */
    openSignInModal() {
        if (this.options.closeOnSigninOpen) {
            this.closeModal();
            return Promise.resolve();
        }

        return getResources().then((resources) => {
            Event.emit('modal.open', {
                options: Object.assign(resources.data.LOGIN_MODAL_OPTIONS),
            });
        });
    }

    /**
     * Close registration pop-up
     */
    closeModal() {
        Event.emit('modal.close');
    }

    /**
     * Destroy is called automatically after the component is being removed from the DOM
     * You must always destroy the listeners attached to an element to avoid any memory leaks
     */
    destroy() {
        off('form.submit.success', this.element);
        if (this.selectors.socialLoginButtons.length > 0) {
            off('click', this.selectors.socialLoginButtons);
        }
    }
}
